import React, { useState, useEffect } from 'react';
import { Heart, Users, Rainbow, ArrowRightCircle, ArrowDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import WhatsAppCommunityBanner from '../components/utilities/WhatsAppCommunityBanner';

const GenderSexualIdentityPage: React.FC = () => {
    const [currentText, setCurrentText] = useState("Affirming");
    const textCycle = ["Affirming", "Validating", "Empowering", "Celebrating"];

    const navigate = useNavigate();

    useEffect(() => {
        const interval = setInterval(() => {
            const currentIndex = textCycle.indexOf(currentText);
            const nextIndex = (currentIndex + 1) % textCycle.length;
            setCurrentText(textCycle[nextIndex]);
        }, 2000);

        return () => clearInterval(interval);
        // eslint-disable-next-line
    }, [currentText]);

    const identityCards = [
        {
            icon: <Rainbow size={48} color="#8E44AD" />,
            title: "Understanding Identity",
            description: "Your identity is a unique, complex journey. There's no single 'right' way to experience gender or sexuality.",
            image: "/images/lgbt.png"
        },
        {
            icon: <Heart size={48} color="#E2B010" />,
            title: "Queer-Affirmative Support",
            description: "We provide a safe, compassionate space that validates your experiences and challenges heteronormative assumptions.",
            image: "/images/queer1.jpg"
        },
        {
            icon: <Users size={48} color="#A1ADA5" />,
            title: "Community & Connection",
            description: "Find support, resources, and understanding in a space that celebrates your unique identity and journey.",
            image: "/images/queer3.jpg"
        }
    ];

    const communityTestimonials = [
        {
            quote: "As a non-binary person, finding a space that truly understands the complexity of my gender identity has been life-changing. Here, I'm not asked to fit into a box, but celebrated for my authentic self.",
            avatar: "/images/avatar1.png"
        },
        {
            quote: "Growing up in a conservative community, I struggled with accepting my sexuality. This support network helped me not just accept myself, but feel proud and empowered. I've learned that my worth isn't defined by others' expectations.",
            avatar: "/images/avatar2.png"
        },
        {
            quote: "Transitioning is a deeply personal journey. Having professionals who approach my experience with genuine empathy, respect, and deep understanding has been crucial in my healing and self-discovery process.",
            avatar: "/images/avatar3.png"
        }
    ];

    return (
        <>
            <Helmet>
                {/* Basic Meta Tags */}
                <title>Compassionate Queer Identity & Mental Health Support | Greh Pravesh Therapy</title>
                <meta name="description" content="Affirming mental health support for LGBTQIA+ individuals. Safe space to explore identity and thrive. Call 8591049845 for care." />

                {/* Open Graph / Social Media Tags */}
                <meta property="og:title" content="Queer-Affirmative Mental Health Support" />
                <meta
                    property="og:description"
                    content="A supportive journey towards understanding, accepting, and celebrating your unique gender and sexual identity. Personalized, compassionate care for LGBTQIA+ community."
                />
                <meta property="og:type" content="healthcare.service" />
                <meta property="og:url" content="https://grehpraveshtherapy.com/gender-sexual-identity" />
                <meta property="og:image" content="/images/pride-support.png" />

                {/* Twitter Card Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Compassionate Queer Identity Support" />
                <meta
                    name="twitter:description"
                    content="Affirming mental health support that validates your journey, challenges heteronormative assumptions, and celebrates diversity."
                />
                <meta name="twitter:image" content="/images/pride-support.png" />

                {/* Schema Markup */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "MentalHealthService",
                        "name": "Greh Pravesh Therapy - Queer Identity Support",
                        "description": "Specialized, compassionate mental health support for LGBTQIA+ individuals exploring gender and sexual identity.",
                        "offers": {
                            "@type": "Offer",
                            "description": "Personalized counseling and support services",
                            "priceCurrency": "INR",
                            "price": "Varies"
                        },
                        "areaServed": {
                            "@type": "GeoCircle",
                            "geoMidpoint": {
                                "@type": "GeoCoordinates",
                                "latitude": "YOUR_LATITUDE",
                                "longitude": "YOUR_LONGITUDE"
                            },
                            "geoRadius": "50 km"
                        },
                        "specialty": [
                            "LGBTQIA+ Mental Health",
                            "Gender Identity Support",
                            "Sexual Identity Counseling"
                        ],
                        "image": "/images/pride-support.png",
                        "url": "https://grehpraveshtherapy.com/gender-sexual-identity"
                    })}
                </script>

                {/* Accessibility and Inclusion Metadata */}
                <meta name="keywords" content="queer support, LGBTQIA+ counseling, gender identity, sexual identity, mental health, affirmative therapy" />
            </Helmet>
            <div className="bg-white">
                {/* Landing Section */}
                <section className="relative min-h-screen flex flex-col md:flex-row overflow-hidden bg-gradient-to-br from-purple to-earthybeige">
                    {/* Background layers */}
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-earthybeige to-pink-300 rounded-br-[100%] z-0"></div>
                    <div className="absolute top-0 right-0 w-full h-full bg-contain bg-no-repeat bg-right-top opacity-10 md:opacity-20 z-0"
                        style={{ backgroundImage: "url('/images/pride-support.png')" }}></div>

                    <div className="w-full md:w-7/12 z-10 flex items-center text-center mt-28 md:mt-0 text-center px-6 md:pl-24 md:pr-0 relative">
                        <div className="max-w-2xl w-full space-y-6 md:mt-20">
                            <motion.div
                                initial={{ opacity: 0, x: -50 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6, delay: 0.3 }}
                                className="bg-white/20 inline-block px-4 py-2 rounded-full"
                            >
                                <span className="text-white text-sm font-medium">Queer-Affirmative Mental Health Support</span>
                            </motion.div>

                            <motion.h2
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.6 }}
                                className="text-xl md:text-3xl text-white font-light"
                            >
                                Compassionate Identity Support
                            </motion.h2>

                            <motion.h1
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.9 }}
                                className="text-4xl md:text-4xl text-white font-bold"
                            >
                                Pathways to
                            </motion.h1>

                            <AnimatePresence mode="wait">
                                <motion.h1
                                    key={currentText}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    transition={{ duration: 0.6 }}
                                    className="text-5xl md:text-5xl text-white font-black tracking-tight"
                                >
                                    {currentText}
                                </motion.h1>
                            </AnimatePresence>

                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 1.5 }}
                                className="hidden md:block text-white/90 md:text-xl max-w-xl"
                            >
                                Our specialized approach provides personalized, compassionate support
                                to help you explore, understand, and celebrate your unique identity.
                            </motion.p>

                            <motion.div
                                className="pt-8 space-y-6"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 2.7 }}
                            >
                                <button
                                    onClick={() => navigate('/book-appointment')}
                                    className="bg-white text-earthybeige flex items-center justify-center text-center px-6 py-3 text-sm md:text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all w-full md:w-auto mx-auto">
                                    Start Your Journey
                                    <ArrowRightCircle className="ml-3 w-7 h-7" />
                                </button>

                                <motion.div
                                    className="block md:hidden w-full h-96 md:h-full"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.9, delay: 3 }}
                                >
                                    <img
                                        src="/images/pride-support.png"
                                        alt="relationship support"
                                        className="w-full h-full object-cover rounded-xl opacity-80"
                                    />
                                </motion.div>
                            </motion.div>
                        </div>
                    </div>

                    {/* Desktop Image Section */}
                    <motion.div
                        className="hidden md:block absolute top-0 right-0 w-5/12 h-full z-0"
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.9, delay: 3 }}
                    >
                        <img
                            src="/images/pride-support.png"
                            alt="Identity Support"
                            className="absolute inset-0 w-full h-full object-cover opacity-70"
                        />
                    </motion.div>
                </section>

                {/* Scroll Indicator */}
                <div className="text-center py-4 bg-white">
                    <ArrowDown
                        className="animate-bounce mx-auto text-purple-600"
                        size={32}
                    />
                </div>

                <div className="container mx-auto px-4 py-12">
                    {/* Identity Cards */}
                    <section className="grid md:grid-cols-3 gap-8">
                        {identityCards.map((card, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.2 }}
                                className="bg-white rounded-xl shadow-lg overflow-hidden transform transition-all hover:scale-105 hover:shadow-xl"
                            >
                                <img
                                    src={card.image}
                                    alt={card.title}
                                    className="w-full h-48 object-cover"
                                />
                                <div className="p-6">
                                    <div className="flex justify-center mb-4">
                                        {card.icon}
                                    </div>
                                    <h2 className="text-2xl font-raleway-regular text-charcol text-center mb-4">
                                        {card.title}
                                    </h2>
                                    <p className="text-center font-lato-regular text-sage">
                                        {card.description}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </section>


                    {/* Approach Section */}
                    <section className="mt-16 bg-white rounded-xl shadow-lg overflow-hidden">
                        <div className="grid md:grid-cols-2">
                            <div className="bg-pink-50 bg-opacity-10 p-8 flex flex-col justify-center">
                                <h2 className="text-3xl font-gentium-book-plus-regular text-charcol mb-6">
                                    Our Queer-Affirmative Approach
                                </h2>
                                <p className="font-lato-regular text-sage mb-6">
                                    We recognize the unique challenges and experiences of LGBTQIA+ individuals.
                                    Our approach is rooted in validation, understanding, and celebrating diversity.
                                </p>
                                <button
                                    className="bg-purple-600 text-white px-6 py-2 rounded-full hover:bg-opacity-90 transition-colors font-raleway-regular self-start"
                                >
                                    Learn More
                                </button>
                            </div>
                            <div className="grid md:grid-cols-2 gap-4 p-8">
                                <div className="bg-softwhite rounded-xl p-6 shadow-md">
                                    <Heart size={40} color="#E2B949" className="mx-auto mb-4" />
                                    <h3 className="text-xl font-raleway-regular text-center text-charcol mb-4">
                                        Core Principles
                                    </h3>
                                    <ul className="list-disc list-inside font-lato-regular text-sage">
                                        <li>Non-Judgmental Support</li>
                                        <li>Identity Validation</li>
                                        <li>Intersectional Understanding</li>
                                        <li>Empowerment</li>
                                    </ul>
                                </div>
                                <div className="bg-softwhite rounded-xl p-6 shadow-md">
                                    <Users size={40} color="#A1ADA5" className="mx-auto mb-4" />
                                    <h3 className="text-xl font-raleway-regular text-center text-charcol mb-4">
                                        Support Strategies
                                    </h3>
                                    <ul className="list-disc list-inside font-lato-regular text-sage">
                                        <li>Safe Space Creation</li>
                                        <li>Identity Exploration</li>
                                        <li>Community Connection</li>
                                        <li>Personal Empowerment</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Community Support Section */}
                    <section className="mt-16 bg-purple-100 bg-opacity-10 rounded-xl p-8">
                        <h2 className="text-3xl font-gentium-book-plus-regular text-charcol text-center mb-8">
                            Community Voices
                        </h2>
                        <div className="grid md:grid-cols-3 gap-6">

                            {communityTestimonials.map((testimonial, index) => (
                                <div
                                    key={index}
                                    className="bg-white rounded-xl p-6 shadow-md relative"
                                >
                                    <img
                                        src={testimonial.avatar}
                                        alt="Community Testimonial"
                                        className="w-16 h-16 rounded-full absolute -top-8 left-1/2 transform -translate-x-1/2 border-4 border-white shadow-md"
                                    />
                                    <div className="text-center mt-8">
                                        <p className="font-lato-regular text-sage italic mb-4">
                                            "{testimonial.quote}"
                                        </p>
                                        <h3 className="font-raleway-regular text-charcol">
                                            Anonymous Community Member
                                        </h3>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    <WhatsAppCommunityBanner />

                    <section className="mt-16 bg-pink-200 bg-opacity-10 rounded-xl p-12 text-center">
                        <h2 className="text-3xl font-gentium-book-plus-regular text-charcol mb-6">
                            Your Identity, Your Journey
                        </h2>
                        <p className="text-xl font-lato-regular text-sage max-w-2xl mx-auto mb-8">
                            Every step towards self-understanding and self-acceptance is courageous.
                            We're here to support you with compassion, expertise, and unwavering respect.
                        </p>
                        <div className="flex flex-col md:flex-row justify-center md:space-x-4 space-y-4 md:space-y-0 w-full max-w-md mx-auto">
                            <Link
                                className="w-full md:w-auto bg-earthybeige text-white px-8 py-3 md:py-4 rounded-lg hover:bg-opacity-90 transition-colors font-raleway-regular text-base"
                                to="/book-appointment"
                            >
                                Schedule Consultation
                            </Link>

                        </div>
                    </section>
                </div>
            </div>
        </>
    );
};

export default GenderSexualIdentityPage;